import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FullProjectFinancement, GenericOperationBodyRequest } from '../projectFinancementBody';
import { GenericOperationField } from './fiche-project-operations/GenericOperationField.model';
import { OperationGlobal } from './fiche-project-operations/OperationGlobal.model';
import { Actor } from './fiche-project-operations/subcontractors.model';
import { SuiviProjectFinancement } from '../suivi-projet/suiva-projet.type';
import { CeeProcessingStatus } from '../suivi-projet/ceeProcessingStatus.enum';
import { CeeTravauxStatus } from '../suivi-projet/ceeTravauxStatus.enum';

@Injectable({
  providedIn: 'root'
})
export class FicheProjetService {
  protected apiRoute = environment.RESOURCE_API;
  protected teraApiRoute = environment.TERA_RESOURCE_API;
  protected generic_operation_api = environment.teraApiCatalog.GENERIC_OPERATION_API;
  protected generic_operation_balise_api = environment.teraApiCatalog.GENERIC_OPERATION_BALISE_API;
  protected generic_operation_case_api = environment.teraApiCatalog.GENERIC_OPERATION_CASE_API;
  protected generic_operation_subcontractor_api = environment.teraApiCatalog.GENERIC_OPERATION_SUBCONTRACTOR_API;


  constructor(private _httpClient: HttpClient) { }

  getProjectFinancementById(projectId: string): Observable<FullProjectFinancement> {
    const route = this.apiRoute + environment.apiCatalog.projectFinancement.controllerRoute + environment.apiCatalog.projectFinancement.subRoutes.getProjectFinancementById + projectId;
    return this._httpClient.get<FullProjectFinancement>(route, this.prepareHeader());
  }

  saveProjectFinancement(saveProjectFinancementBody: FullProjectFinancement, validateAndSendToTera, bodyFromDova): Observable<FullProjectFinancement> {
    const route = this.apiRoute + environment.apiCatalog.projectFinancement.controllerRoute + environment.apiCatalog.projectFinancement.subRoutes.saveProjectFinancement + validateAndSendToTera + '/' + bodyFromDova;
    return this._httpClient.put<FullProjectFinancement>(route, saveProjectFinancementBody, this.prepareHeader());
  }

  getGenericOperations(isCee: boolean, isMpr: boolean): Observable<OperationGlobal[]> {
    const route = this.apiRoute + `api/OperationGenerique/GetAllFromTera/${isCee}/${isMpr}`;
    return this._httpClient.get<OperationGlobal[]>(route, this.prepareHeader());
  }

  getGenericOperationBalise(genericOperationBodyRequest: GenericOperationBodyRequest): Observable<GenericOperationField[]> {
    const route = this.apiRoute + environment.apiCatalog.operation.subRoutes.getCeeMprBalisesFromTera;
    return this._httpClient.post<GenericOperationField[]>(route, genericOperationBodyRequest, this.prepareHeader());
  }

  getGenericOperationCase(genericOperationBodyRequest: GenericOperationBodyRequest): Observable<GenericOperationField[]> {
    const route = this.apiRoute + environment.apiCatalog.operation.subRoutes.getGenericOperationCasesFromTera;
    return this._httpClient.post<GenericOperationField[]>(route, genericOperationBodyRequest, this.prepareHeader());
  }

  getSubcontractors(partnerId): Observable<Actor[]> {
    const route = this.teraApiRoute + this.generic_operation_subcontractor_api + partnerId;
    return this._httpClient.get<Actor[]>(route, this.prepareHeader());
  }

  getSuiviProjectFinancementsByStatusTravaux(statut: CeeTravauxStatus[], partnerId?: string | null, mandateId?: string | null, mandateIds?: string[]): Observable<SuiviProjectFinancement[]> {
    const baseUrl =
      this.apiRoute +
      environment.apiCatalog.projectFinancement.controllerRoute +
      environment.apiCatalog.projectFinancement.subRoutes.getSuiviProjectFinancementsByStatusTravaux;

    let params = new HttpParams();
    if (partnerId) {
      params = params.set('partnerId', partnerId);
    }
    if (mandateId) {
      params = params.set('mandateId', mandateId);
    }
    if (mandateIds?.length) {
      mandateIds.forEach(id => params = params.append('mandateIds', id));
    }

    return this._httpClient.post<SuiviProjectFinancement[]>(baseUrl, statut, {
      ...this.prepareHeader(),
      params,
    });
  }

  resetProjectToBeforeRenovation(projectId: string): Observable<void> {
    const route = this.apiRoute + environment.apiCatalog.projectFinancement.controllerRoute + environment.apiCatalog.projectFinancement.subRoutes.resetProjectToBeforeRenovation + projectId;
    return this._httpClient.post<void>(route, null, this.prepareHeader());
  }


  getSuiviProjectFinancements(statut: CeeProcessingStatus[], isAvantTravaux: boolean, isProjetAvalider: boolean, partnerId?: string | null, mandateId?: string | null, hasAhCdcAccess?: boolean, mandateIds?: string[]): Observable<SuiviProjectFinancement[]> {
    const baseUrl =
      this.apiRoute +
      environment.apiCatalog.projectFinancement.controllerRoute +
      environment.apiCatalog.projectFinancement.subRoutes.getSuiviProjectFinancements;

    let params = new HttpParams();
    params = params.set('isAvantTravaux', isAvantTravaux.toString());
    params = params.set('isProjetAvalider', isProjetAvalider.toString());
    if (partnerId) {
      params = params.set('partnerId', partnerId);
    }
    if (mandateId) {
      params = params.set('mandateId', mandateId);
    }
    if (mandateIds?.length) {
      mandateIds.forEach(id => params = params.append('mandateIds', id));
    }
    if (hasAhCdcAccess !== undefined) {
      params = params.set('hasAhCdcAccess', hasAhCdcAccess.toString());
    }

    return this._httpClient.post<SuiviProjectFinancement[]>(baseUrl, statut, {
      ...this.prepareHeader(), // si prepareHeader() retourne { headers: HttpHeaders }
      params,
    });
  }

  getAllOperationsByProjectId(projectId: string): Observable<SuiviProjectFinancement[]> {
    const route = this.teraApiRoute + environment.teraApiCatalog.renovationProjectCeeOperation.baseRoute + environment.teraApiCatalog.renovationProjectCeeOperation.subRoutes.getAllByRenovationProjectId + projectId;
    return this._httpClient.get<SuiviProjectFinancement[]>(route, this.prepareHeader());
  }

  getAllExternalAuditOperations(projectId: string): Observable<SuiviProjectFinancement[]> {
    const route = this.teraApiRoute + environment.teraApiCatalog.renovationProjectCeeOperation.baseRoute + environment.teraApiCatalog.renovationProjectCeeOperation.subRoutes.getAllExternalAuditOperations + projectId;
    return this._httpClient
      .get<SuiviProjectFinancement[] | { cssDone?: SuiviProjectFinancement[]; cssToDo?: SuiviProjectFinancement[] }>(route, this.prepareHeader())
      .pipe(map((res) => this.normalizeExternalAuditOperationsResponse(res)));
  }

  private normalizeExternalAuditOperationsResponse(
    res: SuiviProjectFinancement[] | { cssDone?: SuiviProjectFinancement[]; cssToDo?: SuiviProjectFinancement[] } | null | undefined
  ): SuiviProjectFinancement[] {
    if (!res) return [];
    if (Array.isArray(res)) return res;

    const cssDone = Array.isArray(res.cssDone) ? res.cssDone : [];
    //const cssToDo = Array.isArray(res.cssToDo) ? res.cssToDo : [];
    //return [...cssDone, ...cssToDo];
    return cssDone;
  }


  getInterventionReportByOperationId(
    projectId: string,
    operationId: string
  ): Observable<Blob> {

    const route =
      this.teraApiRoute +
      environment.teraApiCatalog.renovationProjectDocument.baseRoute +
      environment.teraApiCatalog.renovationProjectDocument.subRoutes.generateInterventionReport +
      projectId + '/' + operationId;

    return this._httpClient.get(route, {
      responseType: 'blob'
    });
  }

  downloadRapportBcByOperationId(
    projectId: string,
    operationId: string
  ): Observable<Blob> {
    const route = this.apiRoute + environment.apiCatalog.projectFinancement.controllerRoute + environment.apiCatalog.projectFinancement.subRoutes.downloadRapportBc + projectId + '/' + operationId;
    return this._httpClient.get(route, {
      responseType: 'blob'
    });
  }

  // Convert a before-renovation project to after-renovation using provided payload
  convertToAfterRenovationProject(payload: any): Observable<any> {
    const route = this.teraApiRoute + environment.teraApiCatalog.renovationProject.baseRoute + environment.teraApiCatalog.renovationProject.subRoutes.convertToAfterRenovationProject;
    return this._httpClient.post<any>(route, payload, this.prepareHeader());
  }

  // Save related project documents to SICA temporary folder and return the list
  saveProjectDocumentsToSicaTemporaryFolder(projectId: string): Observable<any[]> {
    const route = this.apiRoute + environment.apiCatalog.blob.controllerRoute + environment.apiCatalog.blob.subRoutes.saveProjectDocumentsToSicaTemporaryFolder + projectId;
    return this._httpClient.post<any[]>(route, this.prepareHeader());
  }
  //Guid companyId, Guid projectId, int partnerId, int? mandataireId = null
  finalizeProjectAndCreateDossier(companyId: string, projectId: string, partnerId: number, mandataireId: number): Observable<any> {
    const route = this.apiRoute + environment.apiCatalog.blob.controllerRoute + environment.apiCatalog.blob.subRoutes.finalizeProjectAndCreateDossier
      + companyId + '/' + projectId + '/' + partnerId + '/' + mandataireId;
    return this._httpClient.post<any>(route, this.prepareHeader());
  }


  private prepareHeader(): Object {
    let headers = new HttpHeaders();
    headers = headers.set('Accept', 'application/json');
    const token = sessionStorage.getItem('simulator_token');
    if (token) {
      headers = headers.set('simulatorToken', token);
    }
    return { headers: headers };
  }
}
